import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import { useSlackInstall, type SlackInstallError } from "@/hooks/use-slack-install";

interface Props {
  canManage: boolean;
  getToken: () => Promise<string>;
}

export function SlackInstallSection({ canManage, getToken }: Props) {
  const { t, i18n } = useTranslation();
  const install = useSlackInstall(getToken);
  const { fetchStatus } = install;

  useEffect(() => {
    if (canManage) void fetchStatus();
  }, [canManage, fetchStatus]);

  const formatDate = (value: string) => new Intl.DateTimeFormat(i18n.language, {
    dateStyle: "medium", timeStyle: "short",
  }).format(new Date(value));
  const errorText = (error: SlackInstallError) => t(`connections:slack.errors.${error}`);

  if (!canManage) {
    return <div className="p-4 text-sm text-muted-foreground">{t("connections:slack.adminOnly")}</div>;
  }

  return (
    <div aria-labelledby="slack-item-heading" className="space-y-4 p-4 text-sm">
      <div aria-live="polite">
        <p className="font-medium">{t("connections:slack.connectionState")}</p>
        {(install.statusLoading || (!install.status && !install.statusError)) && <p>{t("connections:slack.loadingStatus")}</p>}
        {install.statusError && <p role="alert" className="text-destructive">{errorText(install.statusError)}</p>}
        {install.statusError === "failed" && <Button type="button" variant="outline" size="sm" onClick={() => void install.fetchStatus()}>{t("connections:slack.retryStatus")}</Button>}
        {install.status && !install.statusLoading && (
          <div className="space-y-2">
            <p>{t(`connections:slack.state.${install.status.state}`)}</p>
            {install.status.state === "connected" && (
              <dl className="grid gap-2 sm:grid-cols-[auto_1fr]">
                <dt>{t("connections:slack.workspaceId")}</dt><dd className="font-mono">{install.status.workspaceId}</dd>
                <dt>{t("connections:slack.installedAt")}</dt><dd>{formatDate(install.status.installedAt)}</dd>
                <dt>{t("connections:slack.updatedAt")}</dt><dd>{formatDate(install.status.updatedAt)}</dd>
                <dt>{t("connections:slack.scopes")}</dt><dd>{install.status.scopes.join(", ") || t("connections:slack.noScopes")}</dd>
              </dl>
            )}
          </div>
        )}
      </div>

      <div className="space-y-3 border-t pt-4">
        <p className="rounded-md border border-amber-500/40 bg-amber-500/10 p-3 font-medium" role="note">
          {t("connections:slack.capabilityWarning")}
        </p>
        <Button type="button" onClick={async () => {
          const installUrl = await install.generateLink();
          if (installUrl) window.location.assign(installUrl);
        }} disabled={install.linkLoading}>
          {install.linkLoading ? t("connections:slack.generating") : t("connections:slack.generate")}
        </Button>
        {install.linkError && <p role="alert" className="text-destructive">{errorText(install.linkError)}</p>}
      </div>
    </div>
  );
}
